import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  TextInput,
  SelectArrayInput,
  useListContext,
  TopToolbar,
  ShowButton,
  useRefresh,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  FilterButton,
  useTranslate,
  RecordContextProvider
} from 'react-admin';
import {
  Chip,
  Box,
  Typography,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  Fab,
  Card,
  CardActionArea,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Bolt,
  Battery0Bar,
  Battery1Bar,
  Battery2Bar,
  Battery3Bar,
  Battery4Bar,
  Battery5Bar,
  Battery6Bar,
  BatteryFull,
  StopCircle as StopIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ManualStartDialog } from '../manual-charging/ManualStartDialog';
import { ManualStopDialog } from '../manual-charging/ManualStopDialog';
import { DateTimeFilterInput } from '../common/DateTimeFilterInput';
import { InitiatedByChip } from '../common/InitiatedByChip';
import { KioskTransactionLink } from '../common/KioskTransactionLink';

const useSessionFilters = () => {
  const translate = useTranslate();

  return [
    <TextInput key="search" label={translate('resources.active-sessions.filters.search')} source="search" alwaysOn />,
    <SelectArrayInput
      key="initiated_by"
      label={translate('resources.active-sessions.filters.type')}
      source="initiated_by"
      choices={[
        { id: 'app', name: translate('resources.active-sessions.types.app') },
        { id: 'rfid', name: translate('resources.active-sessions.types.rfid') },
        { id: 'admin', name: translate('resources.active-sessions.types.admin') },
        { id: 'ocpp', name: translate('resources.active-sessions.types.ocpp') },
        { id: 'kiosk', name: translate('resources.active-sessions.types.kiosk') }
      ]}
    />,
    <SelectArrayInput
      key="status"
      label={translate('resources.active-sessions.filters.status')}
      source="status"
      choices={[
        { id: 'initiating', name: translate('resources.active-sessions.statuses.initiating') },
        { id: 'charging', name: translate('resources.active-sessions.statuses.charging') },
        { id: 'completing', name: translate('resources.active-sessions.statuses.completing') },
        { id: 'completed', name: translate('resources.active-sessions.statuses.completed') },
        { id: 'failed', name: translate('resources.active-sessions.statuses.failed') }
      ]}
    />,
    <ReferenceArrayInput
      key="site_id"
      label={translate('resources.active-sessions.filters.site')}
      source="site_id"
      reference="sites"
    >
      <AutocompleteArrayInput
        optionText="name"
        label={translate('resources.active-sessions.filters.site')}
        filterToQuery={(searchText: string) => ({ search: searchText })}
      />
    </ReferenceArrayInput>,
    <ReferenceArrayInput
      key="charger_id"
      label={translate('resources.active-sessions.filters.charger')}
      source="charger_id"
      reference="chargers"
    >
      <AutocompleteArrayInput
        optionText="name"
        label={translate('resources.active-sessions.filters.charger')}
        filterToQuery={(searchText: string) => ({ search: searchText })}
      />
    </ReferenceArrayInput>,
    <DateTimeFilterInput key="date_from" label={translate('resources.active-sessions.filters.fromDate')} source="date_from" defaultTime="00:00" />,
    <DateTimeFilterInput key="date_to" label={translate('resources.active-sessions.filters.toDate')} source="date_to" defaultTime="23:59" />
  ];
};

const StatusChip = ({ status }: { status: string }) => {
  const translate = useTranslate();

  const getColor = () => {
    switch (status) {
      case 'initiating': return 'info';
      case 'charging': return 'success';
      case 'completing': return 'warning';
      case 'completed': return 'default';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={translate(`resources.active-sessions.statuses.${status}`, { _: status })}
      color={getColor() as any}
      size="small"
      icon={status === 'charging' ? <Bolt /> : undefined}
    />
  );
};

const BatteryIcon = ({ percentage }: { percentage: number }) => {
  if (percentage <= 0) return <Battery0Bar />;
  if (percentage <= 14) return <Battery1Bar />;
  if (percentage <= 28) return <Battery2Bar />;
  if (percentage <= 42) return <Battery3Bar />;
  if (percentage <= 57) return <Battery4Bar />;
  if (percentage <= 71) return <Battery5Bar />;
  if (percentage <= 85) return <Battery6Bar />;
  return <BatteryFull />;
};

const PowerDisplay = ({ power }: { power: number }) => {
  const kw = (power / 1000).toFixed(1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Bolt sx={{ fontSize: 16, color: power > 0 ? 'success.main' : 'text.disabled' }} />
      <Typography variant="body2" fontWeight={power > 0 ? 'bold' : 'normal'}>
        {kw} kW
      </Typography>
    </Box>
  );
};

const DurationDisplay = ({ seconds }: { seconds: number }) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return <Typography variant="body2">{hours}h {minutes}m</Typography>;
  }
  return <Typography variant="body2">{minutes} min</Typography>;
};

const DesktopListActions = ({ onManualStart }: { onManualStart: () => void }) => {
  const { total } = useListContext();
  const refresh = useRefresh();
  const translate = useTranslate();

  return (
    <TopToolbar>
      <FilterButton />
      <Button
        startIcon={<Bolt />}
        onClick={onManualStart}
        variant="contained"
        size="small"
        sx={{
          backgroundColor: '#00C853',
          '&:hover': {
            backgroundColor: '#00A843',
          },
        }}
      >
        {translate('resources.active-sessions.actions.manualStart')}
      </Button>
      <Button
        startIcon={<RefreshIcon />}
        onClick={refresh}
        variant="outlined"
        size="small"
      >
        {translate('resources.active-sessions.actions.refresh')}
      </Button>
      <Chip
        label={`${total || 0} ${translate('resources.active-sessions.list.activeSessions')}`}
        color="primary"
        size="small"
        sx={{ ml: 1 }}
      />
    </TopToolbar>
  );
};

const MobileListActions = () => {
  const { total } = useListContext();
  const refresh = useRefresh();
  const translate = useTranslate();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0, gap: 1, alignItems: 'center' }}>
      <Chip
        label={`${total || 0} ${translate('resources.active-sessions.list.activeSessions')}`}
        color="primary"
        size="small"
      />
      <IconButton size="small" onClick={refresh}>
        <RefreshIcon />
      </IconButton>
    </Box>
  );
};

const MobileSessionsList = ({ handleOpenManualStop }: { handleOpenManualStop: (record: any) => void }) => {
  const { data } = useListContext();
  const navigate = useNavigate();
  const translate = useTranslate();

  const canStopSession = (status: string): boolean => {
    return ['initiating', 'charging', 'preparing'].includes(status.toLowerCase());
  };

  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
        {translate('ra.navigation.no_results')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {data.map((record: any) => {
        const userName = record.user_name || record.user_email || record.user_id || 'Unknown';
        const truncatedName = userName.length > 20 ? `${userName.substring(0, 20)}...` : userName;
        const startTime = record.start_timestamp
          ? new Date(record.start_timestamp).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '';

        return (
          <RecordContextProvider key={record.id} value={record}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardActionArea
                onClick={() => navigate(`/active-sessions/${record.id}/show`)}
                sx={{ p: 1.5 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight="500" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                    {truncatedName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <StatusChip status={record.status} />
                    {canStopSession(record.status) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenManualStop(record);
                        }}
                        sx={{
                          color: '#F44336',
                          p: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          },
                        }}
                      >
                        <StopIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {record.site_name}{record.charger_name ? ` - ${record.charger_name}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InitiatedByChip resource="active-sessions" initiatedBy={record.initiated_by} />
                  <KioskTransactionLink transactionId={record.kiosk_transaction_id} />
                  <Typography variant="caption" color="text.secondary">
                    {startTime}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          </RecordContextProvider>
        );
      })}
    </Box>
  );
};

const ChargingProgress = ({ delivered, estimated }: { delivered: number; estimated?: number }) => {
  if (!estimated || estimated <= 0) return null;

  const percentage = Math.min((delivered / estimated) * 100, 100);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BatteryIcon percentage={percentage} />
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 8,
              borderRadius: 1,
              '& .MuiLinearProgress-bar': {
                backgroundColor: percentage > 80 ? 'success.main' : 'primary.main'
              }
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {percentage.toFixed(0)}%
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {delivered.toFixed(1)} / {estimated.toFixed(1)} kWh
      </Typography>
    </Box>
  );
};

export const ActiveSessionsList = () => {
  const [manualStartOpen, setManualStartOpen] = useState(false);
  const [manualStopOpen, setManualStopOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const translate = useTranslate();
  const sessionFilters = useSessionFilters();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleOpenManualStart = () => {
    setManualStartOpen(true);
  };

  const handleOpenManualStop = (record: any) => {
    setSelectedSession({
      id: record.id,
      appSessionId: record.app_session_id,
      chargerName: record.charger_name,
      siteName: record.site_name,
      status: record.status,
      energyDelivered: record.energy_delivered,
      currentPower: record.current_power,
      durationSeconds: record.duration_seconds,
    });
    setManualStopOpen(true);
  };

  const canStopSession = (status: string): boolean => {
    return ['initiating', 'charging', 'preparing'].includes(status.toLowerCase());
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <List
        filters={sessionFilters}
        actions={isMobile ? <MobileListActions /> : <DesktopListActions onManualStart={handleOpenManualStart} />}
        empty={isMobile ? false : undefined}
        perPage={25}
        sort={{ field: 'start_timestamp', order: 'DESC' }}
        title={translate('resources.active-sessions.list.title')}
        sx={{
          '& .RaList-content': {
            backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
          },
          ...(isMobile && {
            '& .RaList-main': { margin: 0 },
            '& .RaList-content': {
              backgroundColor: 'transparent !important',
              boxShadow: 'none !important',
              mt: 2,
            },
            '& .RaList-actions': {
              backgroundColor: 'transparent !important',
              p: '0 !important',
              minHeight: 'auto !important',
            },
            '& .RaListToolbar-root': {
              backgroundColor: 'transparent !important',
              flexDirection: 'row !important',
              flexWrap: 'wrap !important',
              alignItems: 'center !important',
              gap: '8px !important',
            },
            '& .RaListToolbar-root > span': { display: 'none' },
            '& .RaListToolbar-root > form, & .RaFilterForm-root': {
              flex: '1 1 100% !important',
              width: '100% !important',
            },
            '& .filter-field, & .RaFilterForm-filterFormInput': {
              width: '100% !important',
            },
            '& .filter-field .MuiFormControl-root': { width: '100% !important' },
            '& .filter-field .MuiInputBase-root': { width: '100% !important' },
            '& .MuiToolbar-root': {
              backgroundColor: 'transparent !important',
              minHeight: 'auto !important',
            },
          }),
        }}
      >
        {isMobile ? (
          <MobileSessionsList handleOpenManualStop={handleOpenManualStop} />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            sx={{
              '& .RaDatagrid-headerCell': {
                backgroundColor: '#F5F5F5',
                fontWeight: 'bold',
              },
              '& .RaDatagrid-row': {
                '&:hover': {
                  backgroundColor: '#F0F7FF',
                },
              },
            }}
          >
            <FunctionField
              label={translate('resources.active-sessions.list.session')}
              render={(record: any) => {
                const sessionId = record.app_session_id || '';
                const truncated = sessionId.length > 20
                  ? `${sessionId.substring(0, 8)}...${sessionId.substring(sessionId.length - 8)}`
                  : sessionId;
                return (
                  <Typography variant="body2" fontFamily="monospace">
                    {truncated}
                  </Typography>
                );
              }}
            />

            <FunctionField
              label={translate('resources.active-sessions.list.type')}
              render={(record: any) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <InitiatedByChip resource="active-sessions" initiatedBy={record.initiated_by} />
                  <KioskTransactionLink transactionId={record.kiosk_transaction_id} />
                </Box>
              )}
            />

            <FunctionField
              label={translate('resources.active-sessions.list.user')}
              render={(record: any) => {
                const userName = record.user_name || record.user_email || record.user_id || 'Unknown';
                return (
                  <Box>
                    <Typography variant="body2" fontWeight="500">
                      {userName}
                    </Typography>
                    {record.user_phone && (
                      <Typography variant="caption" color="text.secondary">
                        {record.user_phone}
                      </Typography>
                    )}
                  </Box>
                );
              }}
            />

            <TextField source="site_name" label={translate('resources.active-sessions.list.site')} />

            <TextField source="charger_name" label={translate('resources.active-sessions.list.charger')} />

            <DateField
              source="start_timestamp"
              label={translate('resources.active-sessions.list.startTime')}
              showTime
            />

            <FunctionField
              label={translate('resources.active-sessions.list.status')}
              render={(record: any) => <StatusChip status={record.status} />}
            />

            <FunctionField
              label={translate('resources.active-sessions.list.actions')}
              render={(record: any) => (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {canStopSession(record.status) && (
                    <Tooltip title={translate('resources.active-sessions.actions.stopSession')}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenManualStop(record)}
                        sx={{
                          color: '#F44336',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          },
                        }}
                      >
                        <StopIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <ShowButton label="" />
                </Box>
              )}
            />
          </Datagrid>
        )}
      </List>

      {isMobile && (
        <Fab
          color="primary"
          onClick={handleOpenManualStart}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: '#00C853',
            '&:hover': { backgroundColor: '#00A843' },
            zIndex: 1100,
          }}
        >
          <Bolt />
        </Fab>
      )}

      <ManualStartDialog
        open={manualStartOpen}
        onClose={() => setManualStartOpen(false)}
      />

      <ManualStopDialog
        open={manualStopOpen}
        onClose={() => setManualStopOpen(false)}
        session={selectedSession}
      />
    </Box>
  );
};

export default ActiveSessionsList;
